/*
 * Corkopia — demo box controller
 *
 * Makes the build path walkable end to end while the catalogue is fixtures.
 *
 * WHAT IS REAL HERE. The box selection is not faked. This feeds the actual
 * `smart-build-engine.js` `plan()` using the actual box tiers from
 * `#CorkBoxDefinitions` and the actual shape factors from
 * `#CorkInventoryEngineConfig`, which are the same two sources my-cork-box.js
 * reads. So the tier that appears, the capacity percentage, the upgrade
 * prompt and the multi-box and manual-review outcomes are all produced by
 * production logic against real geometry.
 *
 * WHAT IS NOT REAL. There is no Shopify variant behind a demo piece, so nothing
 * here touches /cart.js and the journey stops at a clearly-labelled summary
 * instead of a payment page. That boundary is the point, not an oversight.
 *
 * WHY IT DOES NOT LIVE IN my-cork-box.js. That file is on the must-not-modify
 * list, and its whole model is Shopify cart lines keyed by variant. This runs
 * beside it: it uses a separate `data-demo-add` hook so the cart handler never
 * sees a demo piece, and it takes over the shared tray slots only while the demo
 * box has something in it.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'corkopia-demo-box-v1';

  var cards = document.querySelector('[data-demo-specimen]');
  if (!cards) return;

  var engine = window.CorkopiaSmartBuildEngine;
  if (!engine || typeof engine.plan !== 'function') return;

  function parseJSON(id, fallback) {
    var node = document.getElementById(id);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent) || fallback; } catch (error) { return fallback; }
  }

  var boxes = parseJSON('CorkBoxDefinitions', [])
    .filter(function (box) { return box.active !== false; })
    .sort(function (a, b) { return Number(a.sortOrder) - Number(b.sortOrder); });
  var packing = parseJSON('CorkInventoryEngineConfig', {});

  if (!boxes.length) return;

  function packingFactor(shape) {
    var configured = Number((packing.shapeFactors || {})[shape]);
    return configured > 0 ? configured : Number(packing.defaultPackingFactor) || 0.65;
  }

  function money(cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2).replace(/\.00$/, '');
  }

  var state = load();
  var removedStack = [];
  var arrivingPieceId = null;
  var arrivalTimer = null;

  function load() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && Array.isArray(parsed.items) ? parsed : { items: [] };
    } catch (error) {
      return { items: [] };
    }
  }

  function save() {
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (error) { /* private mode */ }
  }

  function animateToBox(button) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var source = button.closest('[data-demo-specimen]');
    var image = source && source.querySelector('.pl-card__frame img');
    var target = document.querySelector('[data-plate-tray-box]') || document.querySelector('[data-smart-box-mount]');
    if (!image || !target || typeof image.getBoundingClientRect !== 'function') return;
    var from = image.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    if (!from.width || !to.width) return;
    var flyer = image.cloneNode(false);
    flyer.className = 'pl-preview-flyer';
    Object.assign(flyer.style, {
      left: from.left + from.width / 2 - 28 + 'px',
      top: from.top + from.height / 2 - 35 + 'px'
    });
    document.body.appendChild(flyer);
    var dx = to.left + to.width / 2 - (from.left + from.width / 2);
    var dy = to.top + to.height * 0.42 - (from.top + from.height / 2);
    var animation = flyer.animate([
      { transform: 'translate3d(0,0,0) rotate(0deg) scale(1)', opacity: 1 },
      { transform: 'translate3d(' + (dx * 0.34) + 'px,' + (dy * 0.18 - 64) + 'px,4rem) rotate(-8deg) scale(.9)', opacity: 1, offset: .38 },
      { transform: 'translate3d(' + (dx * 0.72) + 'px,' + (dy * 0.58 - 46) + 'px,2rem) rotate(7deg) scale(.65)', opacity: 0.96, offset: .72 },
      { transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) rotate(14deg) scale(.3)', opacity: 0.08 }
    ], { duration: 880, easing: 'cubic-bezier(.2,.72,.2,1)', fill: 'forwards' });
    animation.finished.catch(function () {}).finally(function () { flyer.remove(); });
  }

  function markArrival(pieceId) {
    arrivingPieceId = pieceId;
    if (arrivalTimer) window.clearTimeout(arrivalTimer);
    each('[data-plate-tray-box]', function (el) { el.classList.add('is-receiving'); });
    arrivalTimer = window.setTimeout(function () {
      if (arrivingPieceId === pieceId) arrivingPieceId = null;
      each('[data-plate-tray-box]', function (el) { el.classList.remove('is-receiving'); });
      each('[data-plate-tray-pieces] .is-arriving', function (el) { el.classList.remove('is-arriving'); });
    }, 1320);
  }

  function itemFrom(button) {
    var d = button.dataset;
    var l = Number(d.length);
    var w = Number(d.width);
    var h = Number(d.height);
    return {
      pieceId: d.pieceId,
      name: d.name,
      family: d.family,
      image: d.image,
      priceCents: Number(d.priceCents),
      weightOz: Number(d.weightOz),
      dimensions: [l, w, h],
      volumeCuIn: l * w * h * packingFactor(d.shape),
      shape: d.shape
    };
  }

  // ------------------------------------------------------------------ render

  function each(selector, fn) {
    Array.prototype.forEach.call(document.querySelectorAll(selector), fn);
  }

  function boxDimensions(plan, items) {
    if (plan.box) {
      var values = [plan.box.length, plan.box.width, plan.box.height].map(Number);
      if (values.every(function (value) { return Number.isFinite(value) && value > 0; })) {
        return values.join(' × ') + ' in';
      }
    }
    if (items.length && plan.status === 'multi-box') return 'Multiple boxes — see review';
    if (items.length) return 'Confirmed during review';
    return 'Selected after first piece';
  }

  function render() {
    var items = state.items;
    var plan = engine.plan(boxes, items, null, null);
    var subtotal = items.reduce(function (sum, item) { return sum + item.priceCents; }, 0);
    var weightOz = items.reduce(function (sum, item) { return sum + item.weightOz; }, 0);
    var percent = Math.min(100, Math.round((plan.utilization || 0) * 100));
    // No plan means no capacity reading. `manual-review` and `multi-box` both
    // return no utilization, and printing "about 0% full" next to "Shipping
    // review needed" reads as a failure rather than a handover.
    var hasCapacity = plan.status === 'single';

    each('[data-simple-box-count]', function (el) { el.textContent = String(items.length); });
    each('[data-simple-box-weight]', function (el) {
      el.textContent = items.length ? (weightOz / 16).toFixed(1) + ' lb' : '0 lb';
    });
    each('[data-simple-box-subtotal]', function (el) {
      el.textContent = items.length ? money(subtotal) : '—';
    });
    each('[data-simple-box-space]', function (el) {
      if (!items.length) { el.textContent = 'Start your box'; return; }
      el.textContent = hasCapacity ? plan.capacityLabel + ', about ' + percent + '% full' : plan.capacityLabel;
    });
    each('[data-simple-box-current]', function (el) {
      el.textContent = plan.box
        ? plan.box.name
        : items.length
          ? (plan.status === 'multi-box' ? plan.boxes.length + ' boxes' : 'Reviewed by Corkopia')
          : 'Selected automatically';
    });
    each('[data-simple-box-next]', function (el) { el.textContent = nextUpgradeText(plan, items); });
    each('[data-simple-box-fill]', function (el) { el.style.width = (hasCapacity ? percent : 0) + '%'; });
    each('[data-review-box-dimensions]', function (el) { el.textContent = boxDimensions(plan, items); });
    each('[data-plate-tray-box]', function (el) {
      el.setAttribute('data-empty', String(!items.length));
    });
    each('[data-plate-tray-pieces]', function (el) {
      el.innerHTML = items.slice(0, 8).map(function (item, index) {
        var positions = [
          [7, 12, 29, 38], [37, 8, 27, 42], [66, 14, 27, 36], [18, 52, 28, 36],
          [49, 51, 29, 38], [76, 53, 18, 34], [4, 57, 18, 29], [69, 49, 18, 28]
        ];
        var p = positions[index];
        var arrivalClass = item.pieceId === arrivingPieceId ? ' is-arriving' : '';
        return '<img class="pl-tray__piece' + arrivalClass + '" src="' + item.image + '" alt="" data-tray-piece-id="' + item.pieceId + '" style="left:' + p[0] + '%;top:' + p[1] + '%;width:' + p[2] + '%;height:' + p[3] + '%">';
      }).join('');
    });

    // Below the desktop tray breakpoint the existing cart mount becomes the
    // persistent box affordance. Mirror this gated preview state into its
    // presentational slots so responsive review shows the same demo box.
    each('[data-smart-box-mount]', function (el) {
      el.setAttribute('data-preview-mode', 'true');
      el.setAttribute('data-box-state', items.length ? 'filled' : 'empty');
    });
    each('[data-cart-item-count]', function (el) { el.textContent = String(items.length); });
    each('[data-cart-item-label]', function (el) { el.textContent = items.length === 1 ? 'piece' : 'pieces'; });
    each('[data-smart-box-mount] [data-build-weight]', function (el) {
      el.textContent = items.length ? (weightOz / 16).toFixed(1) + ' lb' : '0 lb';
    });
    each('[data-smart-box-mount] [data-cart-subtotal]', function (el) {
      el.textContent = items.length ? money(subtotal) : '$0';
    });
    each('[data-cart-summary-label]', function (el) {
      var name = el.matches('[data-smart-box-mount] *') ? 'Build a Box' : 'My Box';
      el.setAttribute('aria-label', name + ', ' + items.length + (items.length === 1 ? ' piece, ' : ' pieces, ') + money(subtotal));
    });

    each('[data-demo-add]', function (button) {
      var held = items.some(function (item) { return item.pieceId === button.dataset.pieceId; });
      if (button.disabled && !held) return;
      button.textContent = held ? 'In your box' : 'Add to Box';
      button.setAttribute('aria-pressed', String(held));
    });

    renderReview(plan, subtotal, weightOz);
  }

  // The next tier up that still fits, so the tray can say what would happen
  // rather than only what is happening.
  function nextUpgradeText(plan, items) {
    if (!items.length) return 'Add pieces to calculate';
    if (!plan.box) return 'Shipping reviewed by Corkopia';
    var index = boxes.findIndex(function (box) { return String(box.id) === String(plan.box.id); });
    var next = index >= 0 ? boxes[index + 1] : null;
    return next ? next.name + ' if it grows' : 'Largest box';
  }

  // ------------------------------------------------------------------ review

  var dialog = null;

  function ensureDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'pl-demo-review';
    dialog.setAttribute('aria-labelledby', 'DemoReviewTitle');
    document.body.appendChild(dialog);
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
    return dialog;
  }

  function renderReview(plan, subtotal, weightOz) {
    if (!dialog || !dialog.open) return;
    paintReview(plan, subtotal, weightOz);
  }

  function paintReview(plan, subtotal, weightOz) {
    var items = state.items;
    var node = ensureDialog();
    var shipping = plan.shippingCents;

    node.innerHTML =
      '<div class="pl-demo-review__inner">' +
        '<header class="pl-demo-review__head">' +
          '<div>' +
            '<h2 class="pl-display pl-display--section" id="DemoReviewTitle">Your box</h2>' +
            '<p class="pl-demo-review__sub">' + (items.length
              ? items.length + (items.length === 1 ? ' piece' : ' pieces') + ', ' + (weightOz / 16).toFixed(1) + ' lb'
              : 'Nothing in it yet') + '</p>' +
          '</div>' +
          '<button class="pl-demo-review__close" type="button" data-demo-close aria-label="Close">&times;</button>' +
        '</header>' +
        (items.length ? itemsMarkup(items) : emptyMarkup()) +
        (items.length ? summaryMarkup(plan, subtotal, shipping) : '') +
      '</div>';
  }

  function emptyMarkup() {
    return '<p class="pl-demo-review__empty">Add a piece and the box sizes itself around it.</p>' +
      '<div class="pl-demo-review__actions">' +
        '<button class="pl-btn pl-btn--quiet" type="button" data-demo-close>Keep shopping</button>' +
        '<button class="pl-btn pl-btn--quiet" type="button" data-demo-undo' + (removedStack.length ? '' : ' disabled') + '>Undo remove</button>' +
      '</div>';
  }

  function itemsMarkup(items) {
    return '<ul class="pl-demo-review__items">' + items.map(function (item) {
      return '<li>' +
        '<img src="' + item.image + '" alt="" width="56" height="70" loading="lazy">' +
        '<div>' +
          '<span class="pl-demo-review__id">' + item.pieceId + '</span>' +
          '<strong>' + item.name + '</strong>' +
          '<span class="pl-demo-review__dims">' + item.dimensions.join(' × ') + ' in · ' + (item.weightOz / 16).toFixed(1) + ' lb</span>' +
        '</div>' +
        '<b>' + money(item.priceCents) + '</b>' +
        '<button type="button" data-demo-remove="' + item.pieceId + '" aria-label="Remove ' + item.pieceId + '">Remove</button>' +
      '</li>';
    }).join('') + '</ul>';
  }

  function summaryMarkup(plan, subtotal, shipping) {
    var boxLine = plan.box
      ? plan.box.name
      : plan.status === 'multi-box'
        ? plan.boxes.length + ' boxes'
        : 'Reviewed by Corkopia';

    return '<dl class="pl-demo-review__summary">' +
        '<div><dt>Shipping box</dt><dd>' + boxLine + '</dd></div>' +
        '<div><dt>Subtotal</dt><dd>' + money(subtotal) + '</dd></div>' +
        '<div><dt>Shipping</dt><dd>' + (shipping === null || shipping === undefined ? 'Calculated at checkout' : money(shipping)) + '</dd></div>' +
      '</dl>' +
      '<div class="pl-demo-review__actions">' +
        '<button class="pl-btn pl-btn--primary" type="button" data-demo-checkout' + (plan.checkoutReady ? '' : ' disabled') + '>' +
          (plan.checkoutReady ? 'Buy Now' : 'Shipping review needed') +
        '</button>' +
        '<button class="pl-btn pl-btn--quiet" type="button" data-demo-close>Keep shopping</button>' +
        '<button class="pl-btn pl-btn--quiet" type="button" data-demo-undo' + (removedStack.length ? '' : ' disabled') + '>Undo remove</button>' +
        '<button class="pl-btn pl-btn--quiet" type="button" data-demo-clear>Clear box</button>' +
      '</div>' +
      '<p class="pl-demo-review__note">Preview catalogue. Add to Box, totals, removal and box sizing are working. ' +
        'Payment activates when this preview is replaced by real Shopify products with approved prices.</p>';
  }

  // -------------------------------------------------------- weight filtering

  // Seller's Choice weights point at Shopify filter URLs, which only resolve once
  // the boxes are real products. Until then the same links filter the fixture grid
  // in place, keeping the URL honest so the two behave identically later.
  function setupWeightFilter() {
    var links = Array.prototype.slice.call(document.querySelectorAll('[data-weight-filter]'));
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-demo-weight]'));
    var status = document.querySelector('[data-weight-status]');
    if (!links.length || !cards.length) return;

    function selected() {
      try {
        return new URLSearchParams(window.location.search).get('filter.weight') || 'all';
      } catch (error) {
        return 'all';
      }
    }

    function apply(value) {
      var shown = 0;
      cards.forEach(function (card) {
        var show = value === 'all' || card.dataset.demoWeight === value;
        card.hidden = !show;
        if (show) shown += 1;
      });
      links.forEach(function (link) {
        var active = link.dataset.weightFilter === value;
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      if (status) {
        status.textContent = value === 'all'
          ? shown + ' box sizes shown.'
          : shown + ' box shown, ' + value + ' lb.';
      }
    }

    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var value = link.dataset.weightFilter;
        var url = value === 'all'
          ? window.location.pathname
          : window.location.pathname + '?filter.weight=' + value;
        try { window.history.pushState({ weight: value }, '', url); } catch (error) { /* file:// */ }
        apply(value);
      });
    });

    window.addEventListener('popstate', function () { apply(selected()); });
    apply(selected());
  }

  // ---------------------------------------------------------- piece detail

  var pieceDialog = null;

  function ensurePieceDialog() {
    if (pieceDialog) return pieceDialog;
    pieceDialog = document.createElement('dialog');
    pieceDialog.className = 'pl-demo-piece';
    pieceDialog.setAttribute('aria-labelledby', 'DemoPieceTitle');
    document.body.appendChild(pieceDialog);
    pieceDialog.addEventListener('click', function (event) {
      if (event.target === pieceDialog) pieceDialog.close();
    });
    return pieceDialog;
  }

  // The smallest configured box this piece fits on its own. It is the field the
  // record promises and the one that actually sets a customer's delivery, so it
  // is computed by the engine rather than written into the fixture.
  function minimumBoxFor(item) {
    var box = engine.smallestBox(boxes, [item]);
    return box ? box.name : 'Reviewed by Corkopia';
  }

  function openPiece(button) {
    var d = button.dataset;
    var item = itemFrom(button);
    var node = ensurePieceDialog();
    var held = state.items.some(function (entry) { return entry.pieceId === item.pieceId; });
    var available = d.state === 'available';

    node.innerHTML =
      '<div class="pl-demo-piece__inner">' +
        '<button class="pl-demo-review__close pl-demo-piece__close" type="button" data-demo-piece-close aria-label="Close">&times;</button>' +
        '<div class="pl-demo-piece__media">' +
          '<img src="' + d.imageLarge + '" alt="Cork ' + d.family.toLowerCase() + ', catalogue ' + item.pieceId + '" width="900" height="1125">' +
        '</div>' +
        '<div class="pl-demo-piece__body">' +
          '<p class="pl-eyebrow">Specimen ' + item.pieceId + '</p>' +
          '<h2 class="pl-display pl-display--section" id="DemoPieceTitle">' + item.name + '</h2>' +
          '<p class="pl-demo-piece__price">' + (available ? money(item.priceCents) : 'Claimed') + '</p>' +
          '<table class="pl-spec">' +
            row('Family', d.family) +
            row('Size', d.size) +
            row('Maximum length', d.length + ' in') +
            row('Width', d.width + ' in') +
            row('Height', d.height + ' in') +
            row('Weight', (item.weightOz / 16).toFixed(1) + ' lb') +
            row('Minimum box', minimumBoxFor(item)) +
          '</table>' +
          '<div class="pl-demo-piece__actions">' +
            '<button class="pl-btn pl-btn--primary pl-demo-piece__add" type="button" data-demo-add-detail="' + item.pieceId + '"' +
              (available ? '' : ' disabled') + '>' +
              (available ? (held ? 'In your box' : 'Add to Box') : 'Claimed') +
            '</button>' +
            '<button class="pl-btn pl-btn--quiet" type="button" data-demo-buy-detail="' + item.pieceId + '"' +
              (available ? '' : ' disabled') + '>Buy Now</button>' +
          '</div>' +
          '<p class="pl-demo-piece__note">One of one. When this piece sells its listing closes.</p>' +
        '</div>' +
      '</div>';

    if (!node.open) node.showModal();
  }

  function row(label, value) {
    return '<tr><th scope="row">' + label + '</th><td>' + value + '</td></tr>';
  }

  function openReview() {
    var node = ensureDialog();
    var plan = engine.plan(boxes, state.items, null, null);
    var subtotal = state.items.reduce(function (sum, item) { return sum + item.priceCents; }, 0);
    var weightOz = state.items.reduce(function (sum, item) { return sum + item.weightOz; }, 0);
    paintReview(plan, subtotal, weightOz);
    if (!node.open) node.showModal();
  }

  // ----------------------------------------------------------------- events

  // Capture phase, so a demo piece is handled and stopped before the cart
  // controller's document-level listener can act on the same click.
  function toggle(button) {
    var id = button.dataset.pieceId;
    var index = state.items.findIndex(function (item) { return item.pieceId === id; });
    if (index >= 0) {
      removedStack.push(state.items[index]);
      state.items.splice(index, 1);
      if (arrivingPieceId === id) arrivingPieceId = null;
    } else {
      animateToBox(button);
      markArrival(id);
      state.items.push(itemFrom(button));
    }
    save();
    render();
  }

  function addAndReview(button) {
    var held = state.items.some(function (item) { return item.pieceId === button.dataset.pieceId; });
    if (!held) toggle(button);
    openReview();
  }

  document.addEventListener('click', function (event) {
    var add = event.target.closest('[data-demo-add]');
    if (add) {
      event.preventDefault();
      event.stopPropagation();
      toggle(add);
      return;
    }

    var buyNow = event.target.closest('[data-demo-buy-now]');
    if (buyNow) {
      event.preventDefault();
      event.stopPropagation();
      var buyCard = buyNow.closest('[data-demo-specimen]');
      var buyButton = buyCard && buyCard.querySelector('[data-demo-add]');
      if (buyButton) addAndReview(buyButton);
      return;
    }

    // Opening the piece rather than following the card's fallback href.
    var open = event.target.closest('[data-demo-open]');
    if (open) {
      event.preventDefault();
      event.stopPropagation();
      var card = open.closest('[data-demo-specimen]');
      var button = card && card.querySelector('[data-demo-add]');
      if (button) openPiece(button);
      return;
    }

    var detailAdd = event.target.closest('[data-demo-add-detail]');
    if (detailAdd) {
      event.preventDefault();
      event.stopPropagation();
      var pieceId = detailAdd.getAttribute('data-demo-add-detail');
      var source = document.querySelector('[data-demo-add][data-piece-id="' + pieceId + '"]');
      if (source) {
        toggle(source);
        detailAdd.textContent = state.items.some(function (entry) { return entry.pieceId === pieceId; })
          ? 'In your box'
          : 'Add to Box';
      }
      return;
    }

    var detailBuy = event.target.closest('[data-demo-buy-detail]');
    if (detailBuy) {
      event.preventDefault();
      event.stopPropagation();
      var detailPieceId = detailBuy.getAttribute('data-demo-buy-detail');
      var detailSource = document.querySelector('[data-demo-add][data-piece-id="' + detailPieceId + '"]');
      if (detailSource) {
        if (pieceDialog && pieceDialog.open) pieceDialog.close();
        addAndReview(detailSource);
      }
      return;
    }

    if (event.target.closest('[data-demo-piece-close]')) {
      event.preventDefault();
      event.stopPropagation();
      if (pieceDialog && pieceDialog.open) pieceDialog.close();
      return;
    }

    var remove = event.target.closest('[data-demo-remove]');
    if (remove) {
      event.preventDefault();
      event.stopPropagation();
      var removeId = remove.getAttribute('data-demo-remove');
      var removed = state.items.find(function (item) { return item.pieceId === removeId; });
      if (removed) removedStack.push(removed);
      state.items = state.items.filter(function (item) { return item.pieceId !== removeId; });
      save();
      render();
      openReview();
      return;
    }

    if (event.target.closest('[data-demo-undo]')) {
      event.preventDefault();
      event.stopPropagation();
      var restored = removedStack.pop();
      if (restored && !state.items.some(function (item) { return item.pieceId === restored.pieceId; })) state.items.push(restored);
      save();
      render();
      openReview();
      return;
    }

    if (event.target.closest('[data-demo-clear]')) {
      event.preventDefault();
      event.stopPropagation();
      removedStack = removedStack.concat(state.items.slice());
      state.items = [];
      save();
      render();
      openReview();
      return;
    }

    if (event.target.closest('[data-demo-close]')) {
      event.preventDefault();
      event.stopPropagation();
      if (dialog && dialog.open) dialog.close();
      return;
    }

    if (event.target.closest('[data-demo-checkout]')) {
      event.preventDefault();
      event.stopPropagation();
      var button = event.target.closest('[data-demo-checkout]');
      button.disabled = true;
      button.textContent = 'Real product required';
      return;
    }

    // The tray's own review trigger belongs to the cart controller, which would
    // open an empty panel because no demo piece is a cart line.
    if (event.target.closest('[data-review-build-open], [data-cork-box-open]')) {
      event.preventDefault();
      event.stopPropagation();
      openReview();
    }
  }, true);

  // my-cork-box.js re-renders the shared tray from an empty Shopify cart on
  // visibility changes and writes zeros over these slots. Reassert whenever a
  // write that disagrees with the demo box lands.
  //
  // The comparison is the loop guard, deliberately rather than a "currently
  // writing" flag: our own write leaves the counter equal to the item count, so
  // the mutation it causes exits on the next line. A flag would have to be
  // cleared on a timer, and an rAF-based clear never fires in a hidden tab,
  // which would wedge this permanently for anyone who backgrounds the page.
  var counter = document.querySelector('[data-simple-box-count]');
  if (counter && typeof MutationObserver === 'function') {
    new MutationObserver(function () {
      if (!state.items.length) return;
      if (counter.textContent.trim() === String(state.items.length)) return;
      render();
    }).observe(counter, { childList: true, characterData: true, subtree: true });
  }

  setupWeightFilter();
  render();
})();
